import { useEffect, useState } from 'react';
import type { ContextFrameNamesModal } from '@nike/privacy-types';
import {
  RELIC_ACTION_TYPES_NAME,
  addRelicAnalyticsPageAction,
  getRelicActionName,
  getRelicConsent,
  getRelicUserType,
} from '@nike/privacy-shared-logic';
import { useEventListener } from '@nike/privacy-shared-react';

import { getPrivacyCoreCustomEventNames } from '../helpers/customEventNames';

/**
 * @name usePermissionsUpdated
 * @description - Custom hook which listen event about permissions updates.
 * @param {Object} args - The object of parameters.
 * @param {ContextFrameNamesModal | undefined} args.contextFrameName - A namespace to retrieve interactions from.
 * @param {function} togglePromptIsRequired - togglePromptIsRequired setter.
 * @param {RELIC_ACTION_TYPES_NAME | undefined} args.relicActionName - Relic event name to get correct event.
 * @param {boolean} args.isLoggedIn - user login status.
 * @param {boolean} args.reloadAfterUpdateIsRequired - tells whether a page reload is required after updating the data.
 * @returns {void}
 */
export const usePermissionsUpdated = ({
  contextFrameName,
  togglePromptIsRequired,
  relicActionType,
  isLoggedIn,
  reloadAfterUpdateIsRequired,
}: {
  contextFrameName: ContextFrameNamesModal | undefined;
  togglePromptIsRequired: (value: boolean) => void;
  relicActionType: `${RELIC_ACTION_TYPES_NAME}` | undefined;
  isLoggedIn: boolean;
  reloadAfterUpdateIsRequired: boolean;
}): void => {
  // Need to use state value to ensure that after retrieving event listener will be used correct values from a state
  const [permissionsUpdated, setPermissionsUpdated] = useState(false);
  const [permissionsUpdatedError, setPermissionsUpdatedError] = useState(false);

  useEventListener(getPrivacyCoreCustomEventNames(contextFrameName).PERMISSIONS_UPDATED, () => {
    setPermissionsUpdated(true);
  });

  useEventListener(
    getPrivacyCoreCustomEventNames(contextFrameName).PERMISSIONS_UPDATE_ERROR,
    () => {
      setPermissionsUpdatedError(true);
    },
  );

  // Need to do this for send actual data to Relic
  useEffect(() => {
    if (permissionsUpdated) {
      togglePromptIsRequired(false);

      addRelicAnalyticsPageAction(getRelicActionName(relicActionType), {
        user_type: getRelicUserType(isLoggedIn),
        consent: getRelicConsent(),
      });

      reloadAfterUpdateIsRequired && window.location.reload();
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [permissionsUpdated]);

  useEffect(() => {
    if (permissionsUpdatedError) {
      togglePromptIsRequired(false);

      addRelicAnalyticsPageAction(getRelicActionName(relicActionType), {
        user_type: getRelicUserType(isLoggedIn),
        consent: '',
      });
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [permissionsUpdatedError]);
};
